import { ReactNode, useEffect, useRef, useState } from 'react';

import {
  Active,
  AutoScrollOptions,
  DndContext,
  DragEndEvent,
  MouseSensor,
  PointerSensor,
  pointerWithin,
  useDndMonitor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';

import { Button } from '@components/button';
import { PortalDragOverlay } from '@components/drag-and-drop';
import { FieldType, FieldTypeKind, createFieldOfKind } from '@components/form/v2';
import { IconButton } from '@components/icon-button';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconDecimalFloat } from '@components/icons/decimal-float';
import { IconEdit } from '@components/icons/edit';
import { IconOpenPreview } from '@components/icons/open-preview';
import { IconStatusIndicator } from '@components/icons/status-indicator';
import { Spinner } from '@components/loading-spinner';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { Keys } from '@components/types/keyboard';
import { useKeyPress } from '@hooks/use-keypress';
import { useWindowDimensions } from '@hooks/use-window-dimensions';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { useFormBuilderContext } from './context';
import { EXTRA_DROPZONE_ID, FieldList } from './field-list';
import { FieldProperties } from './field-properties';
import { getFieldConfigForKind } from './field-registry';
import { FieldsSidebar } from './fields-sidebar';
import { PreviewFields } from './preview-fields';
import { StateField } from './state';
import { FormBuilderPortal, animations } from './top';
import { TreeIcon, TreeIconGroup, colorMap, iconColorMap } from './tree-view';
import { mergePredefinedOptions } from './utils';
import { ViewportActions } from './viewport-actions';

export type FormBuilderContentProps = {
  onSave(value?: string): Promise<void>;
  extraFieldGroups?: ReactNode;
  cancelButtonProps: {
    href?: string;
    onClick?: () => void;
  };
};

export const FormBuilderContent = (props: FormBuilderContentProps) => {
  const { onSave, extraFieldGroups, cancelButtonProps } = props;
  const {
    store: { actions, useIsEditMode, useIsSaving, useFormType },
    overlayPanel,
    closeOverlayPanel,
  } = useFormBuilderContext();

  const builderRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number | undefined>(undefined);

  const isEditing = useIsEditMode();
  const isSaving = useIsSaving();
  const formType = useFormType();
  const dimensions = useWindowDimensions();

  useKeyPress({
    key: Keys.Escape,
    handler: (event) => {
      event.stopPropagation();
      actions.unselect();
    },
  });

  useEffect(() => {
    const el = builderRef.current;
    if (!el) return;
    const containerTop = el.getBoundingClientRect().y;
    // Measure against the scroll parent, not the viewport: in a modal the parent
    // is inset, so viewport height overshoots and the content overflows its parent.
    const parentBottom = el.parentElement?.getBoundingClientRect().bottom ?? dimensions.height;
    setContainerHeight(parentBottom - containerTop);
  }, [dimensions.height]);

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 5,
    },
  });
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 10,
      tolerance: 10,
    },
  });
  const sensors = useSensors(pointerSensor, mouseSensor);

  const handleDragInsertField = (event: DragEndEvent) => {
    if (!event.over) return;
    const kind = event.active.data.current?.kind as FieldTypeKind;
    const props = event.active.data.current?.createProps;

    if (event.over.id === 'first-insert' || event.over.id === EXTRA_DROPZONE_ID) {
      actions.addField(kind, props);
      return;
    }

    const newField = createFieldOfKind(kind, props);
    actions.insertField(newField, event.over);
    actions.selectField(newField.id);
  };

  const handleDragMoveField = (event: DragEndEvent) => {
    if (event.over?.id === EXTRA_DROPZONE_ID) {
      actions.moveFieldToEnd(event.active);
      return;
    }

    if (!event.over?.data.current || !event.active.data.current) {
      return;
    }

    actions.moveField(event.active, event.over);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const activeId = event.active.id.toString();

    if (activeId.startsWith('insert-')) {
      handleDragInsertField(event);
      return;
    }

    if (activeId.startsWith('selected-')) {
      handleDragMoveField(event);
      return;
    }
  };

  return (
    <>
      <PortalComponents onSave={onSave} cancelButtonProps={cancelButtonProps} />
      <div ref={builderRef} id="builder-ref">
        {isSaving && <Spinner />}
        <Content style={{ height: containerHeight }}>
          <DndContext
            autoScroll={autoScrollConfig}
            sensors={sensors}
            collisionDetection={pointerWithin}
            onDragEnd={handleDragEnd}
          >
            <DragOverlay />
            <FieldsSidebar extraFieldGroups={extraFieldGroups} />
            <Middle
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) {
                  actions.unselect();
                }
              }}
            >
              <FieldListContainer>
                <FieldList />
                <BottomPad />
              </FieldListContainer>
            </Middle>
          </DndContext>
          <AnimatePresence>
            {overlayPanel && (
              <OverlayPanelContainer {...animations.overlayPanel}>
                {overlayPanel.render({ close: closeOverlayPanel ?? (() => {}) })}
              </OverlayPanelContainer>
            )}
          </AnimatePresence>
          {formType === 'form' ? (
            <FieldProperties />
          ) : (
            <div
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) {
                  actions.unselect();
                }
              }}
            />
          )}
        </Content>
        <AnimatePresence initial={false}>{!isEditing && <RenderPreview />}</AnimatePresence>
      </div>
    </>
  );
};

const DragOverlay = () => {
  const { store } = useFormBuilderContext();
  const [active, setActive] = useState<Active | null>(null);

  useDndMonitor({
    onDragStart: (event) => {
      setActive(event.active);
    },
    onDragCancel: () => setActive(null),
    onDragEnd: () => setActive(null),
  });

  useEffect(() => {
    if (active) {
      document.documentElement.style.cursor = 'grab';
    } else {
      document.documentElement.style.cursor = '';
    }
  }, [active]);

  if (!active) {
    return null;
  }
  const activeId = active.id.toString();

  if (activeId.startsWith('insert-')) {
    const kind = active.data.current?.kind as FieldTypeKind;
    const extraProps = active.data.current?.createProps ?? {};
    const field = createFieldOfKind(kind, extraProps);
    return <RenderDragOverlay field={field} />;
  } else if (activeId.startsWith('selected-')) {
    const field = store.getState().fieldMap[activeId.split('selected-')[1]];
    return <RenderDragOverlay field={field} />;
  }

  return null;
};

const RenderDragOverlay = ({ field }: { field: FieldType | StateField }) => {
  const { TreeDisplay, Icon } = getFieldConfigForKind(field.kind);
  if (!TreeDisplay) {
    return null;
  }

  const IconToUse = field.kind === 'input-number' && field.isDecimal ? IconDecimalFloat : Icon;

  return (
    <PortalDragOverlay modifiers={[snapCenterToCursor]}>
      <OverlayContainer>
        <div>
          <TreeIconGroup>
            <TreeIcon $color={colorMap[field.kind]}>
              <IconToUse size={16} color={iconColorMap[field.kind]} />
            </TreeIcon>
          </TreeIconGroup>
          <TreeDisplay field={field as any} />
        </div>
      </OverlayContainer>
    </PortalDragOverlay>
  );
};

const OverlayContainer = styled(motion.div)({
  background: theme.color.backgroundLightDark,
  cursor: 'grab',
  boxShadow: theme.shadow.fancy,
  border: `${pxToRem(1)} solid ${theme.color.border10}`,
  borderRadius: pxToRem(4),
  padding: `${pxToRem(8)} ${pxToRem(8)}`,
  minHeight: theme.base.height,
  display: 'flex',
  alignItems: 'center',
  maxWidth: pxToRem(240),
  '> div': {
    flexGrow: 1,
    display: 'flex',
    gap: pxToRem(4),
    alignItems: 'center',
    pointerEvents: 'none',
  },
});

const PortalComponents = (props: FormBuilderContentProps) => {
  const { onSave, cancelButtonProps } = props;
  const { t } = useScopedTranslate(locale);
  const {
    store: {
      actions,
      useIsEditMode,
      useIsSaving,
      useViewport,
      useIsDirty,
      useHasIncompleteConditions,
    },
  } = useFormBuilderContext();

  const isEditing = useIsEditMode();
  const isSaving = useIsSaving();
  const selectedViewport = useViewport();
  const isDirty = useIsDirty();
  const hasIncompleteConditions = useHasIncompleteConditions();
  const isDraft = !isDirty || isSaving;
  return (
    <>
      {!isEditing && (
        <FormBuilderPortal portalId="backarrow-portal">
          <IconButton
            data-cy="fullscreen-preview-back"
            icon={<IconArrowLeft />}
            variant="ghost-purple"
            onClick={actions.closePreview}
          />
        </FormBuilderPortal>
      )}
      <FormBuilderPortal portalId="viewport-portal">
        <AnimatePresence initial={false}>
          {!isEditing && (
            <motion.div {...animations.viewport}>
              <ViewportActions
                selectedViewport={selectedViewport}
                onViewportSelect={actions.editViewport}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </FormBuilderPortal>
      <FormBuilderPortal portalId="draft-portal">
        <AnimatePresence initial={false}>
          {!isDraft && isEditing && (
            <motion.div {...animations.draft}>
              <Stack
                orientation="horizontal"
                align="center"
                space={1}
                style={{ height: theme.base.height }}
              >
                <IconStatusIndicator color="elementIndigo100" />
                <Text font="semibold14" color="elementIndigo100" text={t('unsavedChanges')} />
              </Stack>
            </motion.div>
          )}
        </AnimatePresence>
      </FormBuilderPortal>
      <FormBuilderPortal portalId="cancel-save-portal">
        <Stack space={2} orientation="horizontal" align="center">
          <AnimatePresence initial={false}>
            {isEditing && cancelButtonProps && (
              <motion.div {...animations.cancel}>
                <Button
                  isDisabled={isSaving}
                  variant="secondary"
                  text={t('core.cancel')}
                  {...cancelButtonProps}
                />
              </motion.div>
            )}
          </AnimatePresence>
          <Button
            isDisabled={isDraft || hasIncompleteConditions}
            dataCy="formbuilder-save"
            variant="primary-blue"
            text={t('core.save')}
            onClick={() => onSave()}
            tooltip={hasIncompleteConditions ? t('incompleteConditions') : undefined}
          />
        </Stack>
      </FormBuilderPortal>
      <FormBuilderPortal portalId="preview-portal">
        <Button
          isDisabled={isSaving}
          dataCy="formbuilder-preview-edit"
          variant="secondary"
          iconLeft={isEditing ? <IconOpenPreview /> : <IconEdit />}
          onClick={isEditing ? actions.openPreview : actions.closePreview}
          text={isEditing ? t('preview') : t('core.edit')}
        />
      </FormBuilderPortal>
    </>
  );
};

const RenderPreview = () => {
  const {
    store: { actions, useViewport },
    predefinedOptionsLists,
  } = useFormBuilderContext();

  const fields = actions.deserialize();
  const withPredefinedLists = mergePredefinedOptions(fields, predefinedOptionsLists);
  const viewport = useViewport();

  return <PreviewFields viewport={viewport} fields={withPredefinedLists} />;
};

const FIELD_PROPERTIES_COLUMN_FR = 3;
const MIDDLE_COLUMN_FR = 6;
const FIELDS_SIDEBAR_COLUMN_FR = 3;
const TOTAL_COLUMN_FR = FIELDS_SIDEBAR_COLUMN_FR + MIDDLE_COLUMN_FR + FIELD_PROPERTIES_COLUMN_FR;
const FIELD_PROPERTIES_COLUMN_PERCENT = `${(FIELD_PROPERTIES_COLUMN_FR / TOTAL_COLUMN_FR) * 100}%`;

const Content = styled.div({
  display: 'grid',
  gridTemplateColumns: `${FIELDS_SIDEBAR_COLUMN_FR}fr ${MIDDLE_COLUMN_FR}fr ${FIELD_PROPERTIES_COLUMN_FR}fr`,
  overflow: 'hidden',
  background: theme.color.backgroundPrimary,
  position: 'relative',
});

const OverlayPanelContainer = styled(motion.div)({
  position: 'absolute',
  top: 0,
  right: FIELD_PROPERTIES_COLUMN_PERCENT,
  bottom: 0,
  width: pxToRem(440),
  background: theme.color.backgroundLightDark,
  boxShadow: theme.shadow.fancy,
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: pxToRem(24),
  // Below the preview overlay (z-index 2) so entering preview covers the panel
  // without unmounting it; above the builder grid so it sits over the columns.
  zIndex: 1,
});

export const Middle = styled.div({
  overflowY: 'auto',
  display: 'flex',
  justifyContent: 'center',
  scrollBehavior: 'smooth',
  scrollSnapAlign: 'center center',
});

export const FieldListContainer = styled.div({
  margin: pxToRem(24),
  maxWidth: pxToRem(488),
  width: '100%',
});

const BottomPad = styled.div({
  minHeight: pxToRem(80),
});

const locale = {
  en: {
    preview: 'Preview',
    unsavedChanges: 'Unsaved changes',
    incompleteConditions: 'Finish all condition rules before saving',
  },
};

const autoScrollConfig: AutoScrollOptions = {
  acceleration: 800,
};
